/**
 * Channel arithmetic and copy for group broadcasts.
 *
 * A broadcast used to be email and only email, so one number could stand for
 * the audience and every string could say "email" out loud. With SMS in the
 * picker neither is true: "60 guests" read by a host who means email is exactly
 * how the wrong people get texted, and a text costs money per segment where a
 * skipped email costs nothing. Every helper here refuses to blend the two
 * channels into a single figure, and every count it produces is one the API
 * actually reported rather than one the browser guessed.
 *
 * Lives outside the component because it is the part worth testing: there is no
 * render harness in this package (vitest runs in `node`), so the counting and
 * the wording are unit-tested here and the wiring is pinned structurally in
 * components/groups/broadcasts/broadcasts.contract.test.ts.
 */
import type {
  GroupBroadcastResult,
  SendGroupBroadcastResponse,
} from '../types/groups';

export type BroadcastChannel = 'email' | 'sms';

export const EMAIL: BroadcastChannel = 'email';
export const SMS: BroadcastChannel = 'sms';

/** Canonical order, so a channel list is stable enough to use as a compose key. */
export const CHANNEL_ORDER: BroadcastChannel[] = [EMAIL, SMS];

/** Matches the API cap. The email body's 5000 would be 30+ segments of text. */
export const SMS_BODY_MAX = 280;

export function isChannel(value: unknown): value is BroadcastChannel {
  return value === EMAIL || value === SMS;
}

function clean(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * The channels a request will actually run on.
 *
 * Absent, null and empty all collapse to email — identical to the API's own
 * default, and deliberately not "all". An empty array is what a client posts
 * when its checkbox state initialises wrong, and reading that as "everything"
 * is how a roster gets texted by a UI that mounted before its defaults loaded.
 */
export function normalizeChannels(
  channels: readonly string[] | null | undefined,
): BroadcastChannel[] {
  const asked = new Set((channels ?? []).map(clean).filter(isChannel));
  const picked = CHANNEL_ORDER.filter((channel) => asked.has(channel));
  return picked.length > 0 ? picked : [EMAIL];
}

/**
 * Check/uncheck one channel, keeping canonical order.
 *
 * Unlike normalizeChannels this can return an empty list on purpose: the picker
 * has to be able to show "nothing selected" so the host is told to choose,
 * rather than being silently handed email back and emailing on the next click.
 */
export function toggleChannel(
  channels: readonly string[],
  channel: BroadcastChannel,
  on: boolean,
): BroadcastChannel[] {
  const set = new Set(channels.map(clean).filter(isChannel));
  if (on) set.add(channel);
  else set.delete(channel);
  return CHANNEL_ORDER.filter((candidate) => set.has(candidate));
}

/** Compact label for buttons and chips: "email" / "SMS". */
export function shortChannelLabel(channel: string): string {
  return clean(channel) === SMS ? 'SMS' : 'email';
}

/** Where a channel's numbers came from, which decides what we may claim about them. */
export type ChannelPlanSource =
  /** The API's own per-channel plan: every field is real. */
  | 'plan'
  /** Counted from per-guest channel rows: totals are real, the skip reasons are not. */
  | 'results'
  /** A pre-channels API answering an email-only request. */
  | 'legacy';

export interface ChannelAudience {
  channel: BroadcastChannel;
  /** Would receive, or was attempted. */
  reachable: number;
  /** Everyone an opt-out excludes from this channel. Meaningful only when source is 'plan'. */
  suppressed: number;
  /**
   * How that total splits by whose refusal it was: own refused this channel,
   * other refused a different one and policy silences this one too (an email
   * opt-out stops texts, deliberately). Null when the payload does not attribute
   * them, and then no copy may name a channel — telling a host that twelve
   * guests "opted out of texts" when all twelve unsubscribed from an email is a
   * consent claim about people who never saw a text.
   */
  suppressedOwnChannel: number | null;
  suppressedOtherChannel: number | null;
  /** No email / no phone on file. Meaningful only when source is 'plan'. */
  noAddress: number;
  sent: number;
  failed: number;
  estimatedSegments: number | null;
  encoding: string | null;
  estimatedMessages: number | null;
  source: ChannelPlanSource;
}

export interface BroadcastAudience {
  channels: BroadcastChannel[];
  plans: ChannelAudience[];
  /**
   * Requested channels the response said nothing about. Studio and the
   * concierge API deploy separately, so this is a real state: an older API
   * returns one flat list with no way to say which channel it describes. We
   * name the gap and block Send rather than split the list by guesswork.
   */
  unreported: BroadcastChannel[];
}

function count(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function optional(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Which channel an opt-out was made on, read from the skip reason. The API does
 * distinguish the two: "Guest opted out of texts." is somebody who refused SMS,
 * while a bare "Guest opted out." is the trip-wide unsubscribe, held as an email
 * suppression because the link that sets it lives in an email. A reason we
 * cannot place counts as the other channel's, which under-claims a refusal on
 * this one rather than inventing one.
 */
function optOutChannel(reason: string | null | undefined): BroadcastChannel | null {
  const text = clean(reason);
  if (!text.includes('opted out') && !text.includes('opt-out')) return null;
  return /\btexts?\b|\bsms\b/.test(text) ? SMS : EMAIL;
}

/**
 * Split one channel's opt-outs into the ones aimed at it and the ones inherited
 * from a refusal made elsewhere. Null when the per-guest rows do not add up to
 * the plan's own total — a disagreement means we are reading reasons the API no
 * longer writes, and an unattributed number is honest where a wrong one is not.
 */
function splitOptOuts(
  results: readonly GroupBroadcastResult[] | null | undefined,
  channel: BroadcastChannel,
  suppressed: number,
): { own: number; other: number } | null {
  let own = 0;
  let other = 0;
  for (const result of results ?? []) {
    for (const outcome of result.channelResults ?? []) {
      if (clean(outcome.channel) !== channel) continue;
      if (clean(outcome.status) !== 'skipped') continue;
      const refused = optOutChannel(outcome.error);
      if (refused === null) continue;
      if (refused === channel) own += 1;
      else other += 1;
    }
  }
  return own + other === suppressed ? { own, other } : null;
}

function fromPlan(
  response: Pick<SendGroupBroadcastResponse, 'channelPlans' | 'results'>,
  channel: BroadcastChannel,
): ChannelAudience | null {
  const match = (response.channelPlans ?? []).find((plan) => clean(plan.channel) === channel);
  if (!match) return null;
  const suppressed = count(match.suppressed);
  const split = splitOptOuts(response.results, channel, suppressed);
  return {
    channel,
    reachable: count(match.reachable),
    suppressed,
    suppressedOwnChannel: split?.own ?? null,
    suppressedOtherChannel: split?.other ?? null,
    noAddress: count(match.noAddress),
    sent: count(match.sent),
    failed: count(match.failed),
    estimatedSegments: optional(match.estimatedSegments),
    encoding: match.encoding?.trim() || null,
    estimatedMessages: optional(match.estimatedMessages),
    source: 'plan',
  };
}

function tally(
  statuses: readonly string[],
  channel: BroadcastChannel,
  source: ChannelPlanSource,
): ChannelAudience {
  return {
    channel,
    // A dry run marks rows 'preview' and a real send marks them 'sent', so both
    // shapes report the audience the send touched.
    reachable: statuses.filter((status) => status === 'preview' || status === 'sent').length,
    // The payload knows a guest was skipped but not why; printing "0 opted out"
    // from that would be a claim we cannot support, so callers gate on source.
    suppressed: 0,
    suppressedOwnChannel: null,
    suppressedOtherChannel: null,
    noAddress: 0,
    sent: statuses.filter((status) => status === 'sent').length,
    failed: statuses.filter((status) => status === 'failed').length,
    estimatedSegments: null,
    encoding: null,
    estimatedMessages: null,
    source,
  };
}

function fromResults(
  results: readonly GroupBroadcastResult[] | null | undefined,
  channel: BroadcastChannel,
): ChannelAudience | null {
  const statuses: string[] = [];
  let seen = false;
  for (const result of results ?? []) {
    for (const outcome of result.channelResults ?? []) {
      if (clean(outcome.channel) !== channel) continue;
      seen = true;
      statuses.push(clean(outcome.status));
    }
  }
  return seen ? tally(statuses, channel, 'results') : null;
}

function fromLegacy(
  results: readonly GroupBroadcastResult[] | null | undefined,
): ChannelAudience {
  return tally((results ?? []).map((result) => clean(result.status)), EMAIL, 'legacy');
}

/**
 * Per-channel audience for a dry run or a completed send.
 *
 * `requested` is what the composer asked for; the response's own echo wins when
 * it has one, because the API is the thing that decided what ran.
 */
export function summarizeBroadcastAudience(
  response: Pick<SendGroupBroadcastResponse, 'channels' | 'channelPlans' | 'results'>,
  requested: readonly string[],
): BroadcastAudience {
  const echoed = response.channels ?? [];
  const channels = normalizeChannels(echoed.length > 0 ? echoed : requested);
  // A flat, channel-less result list can only be read as email, and only when
  // email is the single thing we asked for. Anything else stays unreported.
  const legacyReadable = channels.length === 1 && channels[0] === EMAIL;

  const plans: ChannelAudience[] = [];
  const unreported: BroadcastChannel[] = [];
  for (const channel of channels) {
    const plan =
      fromPlan(response, channel)
      ?? fromResults(response.results, channel)
      ?? (legacyReadable ? fromLegacy(response.results) : null);
    if (plan) plans.push(plan);
    else unreported.push(channel);
  }
  return { channels, plans, unreported };
}

function countPhrase(channel: BroadcastChannel, n: number): string {
  const noun = channel === SMS ? 'text message' : 'email';
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** "118 emails" / "1 email" / "41 text messages" — a channel is always named. */
export function channelCountPhrase(plan: ChannelAudience): string {
  return countPhrase(plan.channel, plan.reachable);
}

/**
 * "16 text messages sent" — what actually went out, which is not the audience.
 * A completed send whose plan says 18 reachable and 16 sent must not headline
 * 18; the two failures are the part the host needs to see.
 */
export function sentCountPhrase(plan: ChannelAudience): string {
  return `${countPhrase(plan.channel, plan.sent)} sent`;
}

/**
 * "118 emails and 41 text messages" — the confirmation's audience, stated per
 * channel. There is deliberately no helper that adds the two together.
 */
export function audienceSentence(plans: readonly ChannelAudience[]): string {
  const parts = plans.map(channelCountPhrase);
  if (parts.length === 0) return 'nothing';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "Send: 118 email, 41 SMS" */
export function sendButtonLabel(plans: readonly ChannelAudience[]): string {
  if (plans.length === 0) return 'Send';
  return `Send: ${plans.map((plan) => `${plan.reachable} ${shortChannelLabel(plan.channel)}`).join(', ')}`;
}

/** "Sent 118 email · 41 SMS · 2 SMS failed" */
export function sendOutcomeSentence(plans: readonly ChannelAudience[]): string {
  if (plans.length === 0) return 'Nothing sent.';
  const sent = plans.map((plan) => `${plan.sent} ${shortChannelLabel(plan.channel)}`).join(' · ');
  const failed = plans
    .filter((plan) => plan.failed > 0)
    .map((plan) => `${plan.failed} ${shortChannelLabel(plan.channel)} failed`);
  return [`Sent ${sent}`, ...failed].join(' · ');
}

/**
 * The opt-outs on one channel, each attributed to the channel it was made on.
 *
 * An email opt-out silences SMS as well — that is policy, and the right policy —
 * but reporting it as "opted out of texts" tells the host those guests refused a
 * text they were never sent, and a host acts on that: chasing them for a phone
 * number, or dropping them from a channel they never objected to.
 */
function optOutPhrase(plan: ChannelAudience): string {
  const mine = plan.channel === SMS ? 'texts' : 'emails';
  const theirs = plan.channel === SMS ? 'emails' : 'texts';
  const { suppressedOwnChannel: own, suppressedOtherChannel: other } = plan;
  const parts: string[] = [];
  if (own !== null && other !== null) {
    if (own > 0) parts.push(`${own} opted out of ${mine}`);
    if (other > 0) parts.push(`${other} opted out of ${theirs}, which stops ${mine} too`);
  }
  // Unattributed: say only what a suppression is certain to mean, naming no channel.
  if (parts.length === 0) return `${plan.suppressed} who opted out of messages for this trip`;
  return parts.join(', ');
}

/**
 * Who is being dropped on this channel, and why.
 *
 * Silence here is how a host learns after the fact that half the roster has no
 * phone number. Returns null when the payload cannot support the claim — a
 * results-derived plan knows the count of skips but not their reason.
 */
export function unreachableNote(plan: ChannelAudience): string | null {
  if (plan.source !== 'plan') return null;
  const parts: string[] = [];
  if (plan.noAddress > 0) {
    parts.push(`${plan.noAddress} with no ${plan.channel === SMS ? 'phone number' : 'email address'}`);
  }
  if (plan.suppressed > 0) {
    parts.push(optOutPhrase(plan));
  }
  if (parts.length === 0) return null;
  const total = plan.noAddress + plan.suppressed;
  const noun = plan.channel === SMS ? 'text' : 'email';
  return `${total} won't get the ${noun}: ${parts.join(', ')}.`;
}

/**
 * What the text will cost, in the host's terms.
 *
 * GSM-7's accent coverage is arbitrary — é and ñ are free, á and ç are not — so
 * one character pasted from a document silently halves the per-segment ceiling
 * and doubles the bill. The host cannot be expected to know that; showing the
 * computed encoding next to the segment count makes the cause visible while it
 * can still be edited. The numbers come from the API's dry run, never from a
 * second charset table in the browser that would drift from it.
 */
export function smsCostNote(plan: ChannelAudience): string | null {
  if (plan.channel !== SMS || !plan.estimatedSegments) return null;
  const segments = plan.estimatedSegments;
  const messages = plan.estimatedMessages ?? segments * plan.reachable;
  const encoding = plan.encoding ? ` (${plan.encoding})` : '';
  return `~${segments} segment${segments === 1 ? '' : 's'}${encoding} · ~${messages} message${messages === 1 ? '' : 's'} billed`;
}

/** Total the Send button gates on. Never rendered — the per-channel numbers are. */
export function totalSendable(plans: readonly ChannelAudience[]): number {
  return plans.reduce((sum, plan) => sum + plan.reachable, 0);
}

/**
 * The composer's standfirst, matching whatever is actually ticked. The old copy
 * was a hardcoded "Emails every targeted guest", which becomes a lie the moment
 * a channel picker exists.
 */
export function composeHelperText(channels: readonly string[]): string {
  const picked = CHANNEL_ORDER.filter((channel) => channels.map(clean).includes(channel));
  if (picked.length === 0) return 'Pick at least one channel — nothing sends until you do.';
  const verb = picked.length === 2 ? 'Emails and texts' : picked[0] === SMS ? 'Texts' : 'Emails';
  return `${verb} every targeted guest through the shared Tripsby notification workflow.`;
}
